// src/pages/Home.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import AppConst from "../apis/appconst";
import { toast } from "react-toastify";

// react-icons (install with: npm install react-icons)
import {
  FaUserPlus,
  FaNotesMedical,
  FaHospital,
  FaPrescriptionBottleAlt,
  FaProcedures,
  FaPills,
  FaCashRegister,
  FaBed,
  FaClipboardList,
} from "react-icons/fa";

/**
 * IconBubble - colorful circular icon badge
 * Props:
 *  - children: icon element (react-icon or <i className="bi ...">)
 *  - variant: one of 'blue'|'green'|'orange'|'pink'|'violet'|'teal'
 */
function IconBubble({ children, variant = "blue", style = {} }) {
  const variantClass = {
    blue: "bg-ib-blue",
    green: "bg-ib-green",
    orange: "bg-ib-orange",
    pink: "bg-ib-pink",
    violet: "bg-ib-violet",
    teal: "bg-ib-teal",
  }[variant];

  return (
    <>
      <div className={`icon-bubble ${variantClass}`} style={style} aria-hidden="true">
        {children}
      </div>

      <style>{`
        .icon-bubble {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 6px 14px rgba(13,38,76,0.06);
          font-size: 1.25rem;
        }
        .bg-ib-blue { background: linear-gradient(135deg,#d9f0ff,#cfeeff); color: #0b5fa5; }
        .bg-ib-green { background: linear-gradient(135deg,#e6f6ea,#d9f2db); color: #1f7a3a; }
        .bg-ib-orange { background: linear-gradient(135deg,#fff4e0,#ffe8c8); color: #b35b00; }
        .bg-ib-pink { background: linear-gradient(135deg,#ffe8f0,#ffdced); color: #a02a63; }
        .bg-ib-violet { background: linear-gradient(135deg,#f0e9ff,#eadbff); color: #5a2fa8; }
        .bg-ib-teal { background: linear-gradient(135deg,#e8fbfa,#dff8f7); color: #0b7a73; }

        /* small responsive tweak */
        @media (max-width: 576px) {
          .icon-bubble { width: 48px; height: 48px; font-size: 1.05rem; }
        }
      `}</style>
    </>
  );
}

export default function Home() {
  const [data, setData] = useState({});

  useEffect(() => {
  // helper to safely parse and get name from ldata
  const extractNameFromLdata = (ldataRaw) => {
    if (!ldataRaw) return null;
    try {
      const obj = typeof ldataRaw === "string" ? JSON.parse(ldataRaw) : ldataRaw;
      return obj?.fname || obj?.name || obj?.username || obj?.email || null;
    } catch (e) {
      // if it's not JSON, use it as string
      return String(ldataRaw || "") || null;
    }
  };

  // try to read ldata with retries (handles redirect race)
  const waitForLdata = (timeout = 1000, interval = 50) =>
    new Promise((resolve) => {
      const start = Date.now();
      const tick = () => {
        const raw = sessionStorage.getItem("ldata");
        if (raw) return resolve(raw);
        if (Date.now() - start >= timeout) return resolve(null);
        setTimeout(tick, interval);
      };
      tick();
    });

  const showWelcomeIfNeeded = async () => {
    try {
      // wait up to 1s for ldata (fast redirect case)
      const raw = await waitForLdata(1000, 50);
      // if still not present, bail — maybe user isn't logged in
      if (!raw) return;

      // don't show if already shown in this session
      if (sessionStorage.getItem("welcomeShown")) return;

      const name = extractNameFromLdata(raw) || "User";
      toast.success(`👋 Dear ${name}, welcome to HMS System!`, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      sessionStorage.setItem("welcomeShown", "1");
    } catch (e) {
      // silent if anything fails
    }
  };

  // fetch dashboard data (unchanged)
  const initData = async () => {
    try {
      const storedData = sessionStorage.getItem("ldata");
      if (!storedData) return;
      const response = await axios.get(AppConst.BASE_URL + AppConst.GetDashboard, {
        headers: { "Content-Type": "application/json" },
      });
      if (response?.data?.status) setData(response.data.stats || {});
      else if (response?.data?.success) toast.info(response.data.success);
    } catch (err) {
      toast.error("Failed to load dashboard data");
    }
  };

  showWelcomeIfNeeded();
  initData();

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);


  // utility to safely show numeric values
  const show = (v) => (v === null || typeof v === "undefined" ? 0 : v);

  return (
    <div className="page-content">
      <div className="container-fluid">
        {/* Page title */}
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between bg-galaxy-transparent">
              <h4 className="mb-sm-0">Hospital Dashboard</h4>
              <div className="page-title-right">
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <a href="#!">Home</a>
                  </li>
                  <li className="breadcrumb-item active">Hospital Dashboard</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* Top metrics row */}
        <div className="row g-3">
          <MetricCard
            title="Total LAB Patients"
            value={show(data.total_lab_patients)}
            Icon={<FaUserPlus style={{ width: 22, height: 22 }} />}
            variant="blue"
          />
          <MetricCard
            title="Today LAB Patients"
            value={show(data.today_lab_patients)}
            Icon={<FaNotesMedical style={{ width: 22, height: 22 }} />}
            variant="green"
          />
          <MetricCard
            title="Total OPD Patients"
            value={show(data.total_opd_patients)}
            Icon={<FaHospital style={{ width: 22, height: 22 }} />}
            variant="orange"
          />
          <MetricCard
            title="Today OPD Patients"
            value={show(data.today_opd_patients)}
            Icon={<FaPrescriptionBottleAlt style={{ width: 20, height: 20 }} />}
            variant="pink"
          />
        </div>

        <div className="row g-3 mt-1">
          <MetricCard
            title="Total IPD Patients"
            value={show(data.total_ipd_patients)}
            Icon={<FaProcedures style={{ width: 22, height: 22 }} />}
            variant="violet"
          />
          <MetricCard
            title="Today IPD Patients"
            value={show(data.today_ipd_patients)}
            Icon={<FaProcedures style={{ width: 22, height: 22 }} />}
            variant="teal"
          />
          <MetricCard
            title="Total Pharmacy Patients"
            value={show(data.total_pharmacy_patients ?? 0)}
            Icon={<FaPills style={{ width: 22, height: 22 }} />}
            variant="blue"
          />
          <MetricCard
            title="Today Pharmacy Patients"
            value={show(data.today_pharmacy_patients ?? 0)}
            Icon={<FaPills style={{ width: 22, height: 22 }} />}
            variant="green"
          />
        </div>

        <div className="row g-3 mt-1">
          <MetricCard
            title="IPD Today Collections"
            value={show(data.ipd_today_collections)}
            Icon={<FaCashRegister style={{ width: 20, height: 20 }} />}
            variant="orange"
          />
          <MetricCard
            title="LAB Today Collections"
            value={show(data.lab_today_collections)}
            Icon={<FaCashRegister style={{ width: 20, height: 20 }} />}
            variant="violet"
          />
          <MetricCard
            title="OPD Today Collections"
            value={show(data.opd_today_collections)}
            Icon={<FaCashRegister style={{ width: 20, height: 20 }} />}
            variant="teal"
          />
          <MetricCard
            title="Pharmacy Today Collections"
            value={show(data.pharmacy_today_collections ?? 0)}
            Icon={<FaCashRegister style={{ width: 20, height: 20 }} />}
            variant="pink"
          />
        </div>

        <div className="row g-3 mt-1">
          <MetricCard
            title="IPD Total Collections"
            value={show(data.ipd_total_collections)}
            Icon={<FaClipboardList style={{ width: 20, height: 20 }} />}
            variant="blue"
          />
          <MetricCard
            title="LAB Total Collections"
            value={show(data.lab_total_collections)}
            Icon={<FaClipboardList style={{ width: 20, height: 20 }} />}
            variant="green"
          />
          <MetricCard
            title="OPD Total Collections"
            value={show(data.opd_total_collections)}
            Icon={<FaClipboardList style={{ width: 20, height: 20 }} />}
            variant="orange"
          />
          <MetricCard
            title="Pharmacy Total Collections"
            value={show(data.pharmacy_total_collections ?? 0)}
            Icon={<FaClipboardList style={{ width: 20, height: 20 }} />}
            variant="violet"
          />
        </div>

        {/* Critical notice */}
        <div className="row mt-3">
          <div className="col-12">
            <div className="card">
              <div className="card-body p-0">
                <div className="alert alert-warning border-0 rounded-0 m-0 d-flex align-items-center" role="alert">
                  <i className="text-warning me-2 icon-sm" style={{ fontSize: 18 }}>⚠️</i>
                  <div className="flex-grow-1 text-truncate">
                    Critical Notice: Only <b>{show(data.remaining_beds)} beds</b> left!
                  </div>
                  <div className="flex-shrink-0">
                    <a href="#!" className="text-reset text-decoration-underline"><b>Check Now</b></a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Beds metrics */}
        <div className="row g-3 mt-3">
          <MetricCard
            title="Total Beds"
            value={show(data.total_beds)}
            Icon={<FaBed style={{ width: 22, height: 22 }} />}
            variant="violet"
          />
          <MetricCard
            title="Remaining Beds"
            value={show(data.remaining_beds)}
            Icon={<FaBed style={{ width: 22, height: 22 }} />}
            variant="teal"
          />
          {/* placeholders for layout balance */}
          <div className="col-md-6 d-none d-md-block" />
        </div>

        {/* Hidden/placeholder sections (kept for consistency) */}
        <div style={{ display: "none" }}>
          {/* charts and other complex widgets can be added here */}
        </div>
      </div>
    </div>
  );
}

/* MetricCard component - small helper used above */
function MetricCard({ title, value, Icon, variant = "blue" }) {
  return (
    <div className="col-md-3">
      <div className="card card-animate">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <p className="fw-medium text-muted mb-0">{title}</p>
              <h2 className="mt-3 ff-secondary cfs-22 fw-semibold">{value}</h2>
            </div>
            <div>
              <IconBubble variant={variant}>{Icon}</IconBubble>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
